import * as React from "react";
import { cn } from "@/shared/lib/cn";

interface AvatarProps extends React.ComponentPropsWithoutRef<"div"> {
  src?: string | null;
  alt?: string;
  name?: string;
  size?: "sm" | "md" | "lg";
}

const sizeClasses: Record<NonNullable<AvatarProps["size"]>, string> = {
  sm: "h-8 w-8 text-xs",
  md: "h-11 w-11 text-sm",
  lg: "h-14 w-14 text-base",
};

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ src, alt, name, size = "md", className, ...props }, ref) => {
    const initials =
      name
        ?.trim()
        .split(/\s+/)
        .map((segment) => segment[0]?.toUpperCase())
        .slice(0, 2)
        .join("") || "IP";

    return (
      <div
        ref={ref}
        className={cn(
          "relative overflow-hidden rounded-full border border-grid-200 bg-grid-100 text-ink-500",
          "flex items-center justify-center uppercase tracking-[0.12em]",
          sizeClasses[size],
          className
        )}
        {...props}
      >
        {src ? (
          <img
            src={src}
            alt={alt ?? name ?? "User avatar"}
            className="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <span className="font-semibold">{initials}</span>
        )}
      </div>
    );
  }
);
Avatar.displayName = "Avatar";
